import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Slider from 'react-slick';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { toast } from 'react-toastify';
// IMPORTANT: We reuse the main landing page's CSS module for brand consistency.
import styles from './LandingPage.module.css';


import { 
    FaTools, FaFileInvoiceDollar, FaStar, FaArrowRight, 
    FaCheckCircle, FaUsers, FaChartLine, FaMobileAlt, FaTimes, FaBars
} from 'react-icons/fa';
import bizBanner from '../../assets/images/bizbanner.png';
import imageLogo from "../../assets/images/LogoBuildStart03.png";

// --- Reusable Header ---
// We can reuse the same header component from the main landing page for consistency
const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <header className={styles.header}>
      <div className={styles.container}>
        <div className={styles.logo}>
          <img
            src={imageLogo}   // Replace with your logo image path
            alt="Build Start Logo"
            style={{ height: '35px', marginRight: '10px' }}  // Adjust size & spacing as needed
          />
          BUILD START</div>
        <nav className={`${styles.nav} ${isMenuOpen ? styles.navMobile : ''}`}>
          <ul className={styles.navLinks}>
            <li className={styles.closeIcon} onClick={toggleMenu}><FaTimes /></li>
            <li><a href="/">Home Owner</a></li>
            <li className={styles.activeLink} ><a href="/business-landing">For Building Professionals</a></li>
            <li><a href="/for-developers">For Renovators / Developers</a></li>
            <li><a href="/resources-hub">Resources Hub</a></li>
            <li><a href="/contact-support">Support</a></li>
            <li className={styles.mobileAuth}>
              <Link to="/login" className={styles.btnLogin}>Login</Link>
            </li>
            <li className={styles.mobileAuth}>
              <Link to="/select-role" className={styles.btnRegister}>Register</Link>
            </li>
          </ul>
        </nav>
        <div className={styles.headerRight}>
          <Link to="/login" className={styles.btnLogin}>Login</Link>
          <Link to="/select-role" className={styles.btnRegister}>List Your Business</Link>
        </div>
        <div className={styles.menuIcon} onClick={toggleMenu}>
          <FaBars />
        </div>
      </div>
    </header>
  );
};

// --- NEW: Social Proof Engine Component ---
const SocialProofEngine = () => {
    const [notification, setNotification] = useState(null);
    const [isVisible, setIsVisible] = useState(false);

    const mockActivities = [
        { type: 'job_post', text: 'A homeowner in Sydney, NSW just posted a job for a Plumber.' },
        { type: 'search', text: 'Someone in Melbourne, VIC is searching for an Electrician.' },
        { type: 'connection', text: 'We just connected a family in Perth, WA with \'Pro Painters WA\'.' },
        { type: 'job_post', text: 'A builder in Brisbane, QLD just posted a job for a Concreter.' },
        { type: 'search', text: 'Someone in Adelaide, SA is searching for a Carpenter.' },
    ];

    useEffect(() => {
        // This function simulates fetching data from an API
        const fetchLatestActivity = () => {
            const randomIndex = Math.floor(Math.random() * mockActivities.length);
            setNotification(mockActivities[randomIndex]);
            setIsVisible(true);

            // Hide the notification after a few seconds
            setTimeout(() => {
                setIsVisible(false);
            }, 5000); // Visible for 5 seconds
        };
        
        // Poll for new activity every 8 seconds
        const intervalId = setInterval(fetchLatestActivity, 8000);

        // Initial fetch
        fetchLatestActivity();

        // Cleanup on component unmount
        return () => clearInterval(intervalId);
    }, []);

    if (!notification) return null;

    return (
        <div className={`${styles.activityToast} ${isVisible ? styles.toastVisible : ''}`}>
            {notification.text}
        </div>
    );
};


// --- Page-Specific Sections ---

const HeroSection = () => (
    <section className={styles.businessHero}>
        <div className={styles.heroImagesContainer2}>
            <img
              src={bizBanner}
              alt="Business Growth Banner"
              className={styles.heroImageBg}
            />
        </div>
        <div className={styles.container}>
            <div className={styles.businessHeroContent}>
                <h1>Let Quality Leads Come to You.</h1>
                <p>Build Start is an invitation-only directory of the most trusted professionals in your area. We connect you with serious homeowners ready to hire, so you can build your business with confidence.</p>
                


                <div className={styles.heroCtaForm}>
                    <input type="text" placeholder="What's your primary trade?" />
                    <input type="text" placeholder="Enter your postcode" />
                    <Link to="/select-role" className={styles.btnPrimary}>Start Winning Work</Link>
                </div>




            </div>
        </div>
        {/* The background illustration is now handled by CSS for consistency */}
    </section>
);

const HowItWorksSection = () => (
    <section className={`${styles.sectionPadding} ${styles.lightBg}`}>
        <div className={styles.container}>
            <h2 className={styles.sectionTitle}>How Build Start Works for You</h2>
            <div className={styles.howItWorksGrid}>
                <div className={styles.howItWorksStep}>
                    <div className={styles.stepIconWrapper}><FaCheckCircle /></div>
                    <h3>1. Get Quality Job Leads</h3>
                    <p>Receive alerts for real jobs from serious homeowners in your service area. We vet the projects, so you don't waste time on tire-kickers.</p>
                </div>
                <div className={styles.howItWorksStep}>
                    <div className={styles.stepIconWrapper}><FaUsers /></div>
                    <h3>2. Win More Work</h3>
                    <p>Use our professional quoting tools to submit winning bids. Showcase your work and collect verified reviews to build a reputation that stands out.</p>
                </div>
                <div className={styles.howItWorksStep}>
                    <div className={styles.stepIconWrapper}><FaChartLine /></div>
                    <h3>3. Manage Your Business</h3>
                    <p>Our platform is more than just leads. Schedule jobs, send invoices, and manage payments all in one place. Spend less time on admin, more time on the tools.</p>
                </div>
            </div>
        </div>
    </section>
);

const LeadExamplesSection = () => {
  const [jobsData, setJobsData] = useState([]);

  const handleGetJobs = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/Jobs/GetLatestJobs`);
      setJobsData(response.data.slice(0, 10)); // Keep only 10 jobs
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to fetch jobs');
    }
  };

  useEffect(() => {
    handleGetJobs(); // Initial fetch

    const fetchInterval = setInterval(() => {
      handleGetJobs();
    }, 3 * 60 * 1000); // Fetch every 3 minutes

    const shiftInterval = setInterval(() => {
      setJobsData(prevJobs => {
        if (prevJobs.length === 0) return [];
        const newJobs = [...prevJobs];
        const firstJob = newJobs.shift();
        newJobs.push(firstJob);
        return newJobs;
      });
    }, 60 * 1000); // Shift every 1 minute

    return () => {
      clearInterval(fetchInterval);
      clearInterval(shiftInterval);
    };
  }, []);

  const jobSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
        }
      },
      {
        breakpoint: 600,
        settings: {
          slidesToShow: 1,
        }
      }
    ]
  };

  return (
    <section className={styles.leadExamples}>
      <div className={styles.container}>
        <h2 className={`${styles.sectionTitle} ${styles.largeTitle}`}>Live Job Feed</h2>
        {jobsData.length > 0 ? (
          <Slider {...jobSettings}>
            {jobsData.map((job) => (
              <div key={job.job_id} className={styles.jobCard}>
                <h3>{job.name}</h3>
                <p>{job.postcode}</p>
                <p>{job.description}</p>
                <p>Budget: {job.budget || job.budgetAmount}</p>
              </div>
            ))}
          </Slider>
        ) : (
          <p className={styles.textCenter}>Loading jobs...</p>
        )}
      </div>
    </section>
  );
};


const ValuePropositionSection = () => (
    <section className={`${styles.whyBuildStart} ${styles.sectionPadding}`}>
        <div className={styles.container}>
            <h2 className={styles.sectionTitle}>Your All-in-One Business Growth Partner</h2>
            <div className={styles.valueGrid}>
                {/* This section remains structurally the same, but will inherit new, consistent styling */}
                <div className={styles.valueCard}>
                    <FaTools className={styles.valueIcon} />
                    <h3>Qualified Leads</h3>
                    <p>Get access to hundreds of job leads from homeowners who have provided detailed project information. Spend less time quoting and more time working.</p>
                </div>
                <div className={styles.valueCard}>
                    <FaFileInvoiceDollar className={styles.valueIcon} />
                    <h3>Streamline Your Entire Workflow</h3>
                    <p>Our platform isn't just a lead source. Manage quotes, schedule jobs, send invoices, and receive payments all in one place with our powerful business tools.</p>
                </div>
                <div className={styles.valueCard}>
                    <FaStar className={styles.valueIcon} />
                    <h3>Build a Reputation That Wins Jobs</h3>
                    <p>Showcase your best work with a professional online profile. Collect verified reviews from completed jobs to build trust and stand out from the competition.</p>
                </div>
            </div>
        </div>
    </section>
);

const TestimonialSection = () => (
    <section className={`${styles.sectionPadding} ${styles.businessTestimonial}`}>
        <div className={styles.container}>
            <img src="https://via.placeholder.com/100" alt="Hayden, Wattley Air and Electrical" />
            <blockquote>"Build Start was the single best decision for my business. We've seen a 40% increase in qualified leads and can now focus on doing great work instead of chasing clients."</blockquote>
            <cite>Hayden, Wattley Air and Electrical</cite>
        </div>
    </section>
);

const PricingSection = () => (
    
    <section id="pricing" className={`${styles.pricingSection} ${styles.sectionPadding}`}>
        <div className={styles.container}>
             <h2 className={styles.sectionTitle}>Plans Designed for Growth</h2>
             <p className={styles.subheadline}>Choose a subscription that scales with your business. No hidden fees. Cancel anytime.</p>
             <div className={styles.pricingGrid}>
                <div className={styles.pricingCard}>
                    <h3>Starter Pack</h3>
                    <p className={styles.price}>$99/mo</p>
                    <ul>
                        <li>✓ Get Listed in our directory</li>
                        <li>✓ Be searchable immediately</li>
                        <li>✓ Get instant leads as soon new jobs come up</li>
                        <li>✓ Email & App Notifications</li>
                        <li>✓ Grow your business</li>
                    </ul>
                    <Link to="/select-role" className={styles.btnSecondary}>Choose Plan</Link>
                </div>
                 <div className={`${styles.pricingCard} ${styles.featuredPlan}`}>
                    <span className={styles.popularBadge}>Most Popular</span>
                    <h3>Pro Pack</h3>
                    <p className={styles.price}>$140/mo</p>
                    <ul>
                        <li>✓ Everything in Starter</li>
                        <li>✓ Get instant leads as soon new jobs come up</li>
                        <li>✓ Email & App Notifications</li>
                        <li>✓ 4 x Sponsored Social Posts</li>
                        <li>✓ 4 x Sponsored Social Video Posts</li>
                    </ul>
                    <Link to="/select-role" className={styles.btnPrimary}>Choose Plan</Link>
                </div>
                 <div className={styles.pricingCard}>
                    <h3>Corportate Pack</h3>
                    <p className={styles.price}>$999 /mo</p>
                    <ul>
                        <li>✓ Everything in Pro</li>
                        <li>✓ Be Featured on our Landing Page</li>
                        <li>✓ Get Seen with Sponsorted Listings</li>
                        <li>✓ Unlimited Sponsored Social Posts</li>
                        <li>✓ Unlimited Sponsored Social Video Posts</li>
                    </ul>
                    <Link to="/select-role" className={styles.btnSecondary}>Choose Plan</Link>
                </div>
              </div>




        {/* --- NEW PAY AS YOU GO SECTION --- */}
        <div className={styles.payAsYouGoSection}>
            <h3 className={styles.sectionDivider}>Or Pay As You Go</h3>
            <div className={`${styles.pricingCard} ${styles.payAsYouGoCard}`}>
                <h3>Advertising</h3>
                
                {/* --- ADD THE NEW DESCRIPTION HERE --- */}
                <p className={styles.cardDescription}>
                    You can purchase advertising on our Social Media Platform which will 
                    instantly put your brand in front of thousands of targeted, engaged users. 
                </p>

                <ul>
                    <li>
                        <span>Sponsored Non-Video Ad:</span>
                        <span className={styles.priceDetail}>$7.70 <small>incl. GST</small></span>
                    </li>
                    <li>
                        <span>Sponsored Video Ad:</span>
                        <span className={styles.priceDetail}>$9.90 <small>incl. GST</small></span>
                    </li>
                </ul>



                {/* --- 2. ADD THE FOOTNOTE HERE --- */}
                <p className={styles.footnote}>* Ads are valid for one month.</p>
                
                {/* --- 1. WRAP THE BUTTON IN A DIV FOR CENTERING --- */}
                <div className={styles.buttonWrapper}>
                    <Link to="/purchase-ads" className={styles.btnSecondary}>Purchase Ads</Link>
                </div>

            </div>
        </div>

        </div>
    </section>
);


const FinalCtaSection = () => (
    <section className={`${styles.finalCta} ${styles.professionals} ${styles.sectionPadding}`}>
        {/* This section remains structurally the same, but will inherit new, consistent styling */}
        <div className={styles.container}>
            <h2>Ready to Grow Your Business?</h2>
            <p>Join thousands of professionals building their empires on Build Start.</p>
            <Link to="/select-role" className={styles.btnPrimaryLarge}>
                Create Your Free Profile Today <FaArrowRight />
            </Link>
        </div>
    </section>
);



// --- Main Page Component ---
const BusinessGrowthPage = () => {
    return (
        <>
            <Header />
            <main>
                <SocialProofEngine />
                <HeroSection />
                <HowItWorksSection />
                <LeadExamplesSection />
                <ValuePropositionSection />
                <TestimonialSection />
                <PricingSection />
                <FinalCtaSection />
            </main>
            {/* You can reuse your main Footer component here */}
        </>
    );
};


export default BusinessGrowthPage;
